// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Camera QR-code scanning — the optional sensor input for pairing. Uses the rear
 * camera (getUserMedia) plus the native BarcodeDetector to read the pairing QR
 * shown on the other device. Both are feature-detected; when either is missing,
 * or the user denies the camera, the caller falls back to pasting the link, which
 * is a first-class path.
 *
 * Privacy: the video stream is decoded frame-by-frame in this tab and never
 * recorded or uploaded. Every track is stopped on teardown so the OS camera
 * indicator goes out.
 */

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
interface BarcodeDetectorCtor {
  new (opts?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

/** True if this browser can scan a QR from the camera. */
export function supportsScan(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector !== 'undefined' &&
    window.isSecureContext
  );
}

export class QrScanner {
  private stream: MediaStream | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private detector: BarcodeDetectorLike | null = null;
  private stopped = false;

  /**
   * Start scanning into the given <video> element. Calls `onResult` once with the
   * first decoded QR value (then stops), or `onError` if the camera can't start.
   * MUST be called from within a user gesture (the camera permission prompt).
   */
  async start(video: HTMLVideoElement, onResult: (value: string) => void, onError: (message: string) => void): Promise<void> {
    this.stopped = false;
    const Ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
    if (!Ctor) {
      onError('This browser has no QR scanner. Paste the pairing link instead.');
      return;
    }
    try {
      this.detector = new Ctor({ formats: ['qr_code'] });
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      if (this.stopped) {
        this.stop();
        return;
      }
      video.srcObject = this.stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      await video.play().catch(() => undefined);
    } catch (e) {
      const name = (e as Error).name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        onError('Camera access was blocked. Paste the pairing link instead.');
      } else {
        onError('Could not start the camera. Paste the pairing link instead.');
      }
      this.stop();
      return;
    }

    // setInterval, not rAF: a backgrounded tab throttles rAF to a halt.
    this.timer = setInterval(async () => {
      if (this.stopped || !this.detector || video.readyState < 2) return;
      try {
        const codes = await this.detector.detect(video);
        const hit = codes.find((c) => c.rawValue && c.rawValue.length > 0);
        if (hit) {
          const value = hit.rawValue;
          this.stop();
          onResult(value);
        }
      } catch {
        /* transient decode error — keep trying */
      }
    }, 180);
  }

  /** Stop scanning and release the camera (indicator goes out). */
  stop(): void {
    this.stopped = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
      this.stream = null;
    }
  }

  /** True while a live camera stream is running. */
  get active(): boolean {
    return !!this.stream;
  }
}
